import { loadSubstitutions, replaceByValues, tryParseSubstitution, SubstitutionsError } from 'shared/utils/substitution';
import * as QA from 'shared/utils/queryAnalysis';
import { compareArrayContent } from 'shared/utils/utils';
import moment from 'moment';
import { getExcelValuesConverter } from 'shared/excel/converters/index';

export async function resolveFilterSubstitutions(filters = [], loaders) {
    const toLoad = [];

    const result = filters.reduce((out, filter, index) => {
        const { value, name } = filter;
        const { substitutions, hasSubstitutions } = tryParseSubstitution(value);
        const v = hasSubstitutions
            ? { substitutions, value, fieldName: name, index, filter }
            : filter;
        filter.isSubstitution = hasSubstitutions; /* required by the legacy code on the backend */
        hasSubstitutions && toLoad.push(v);
        out.push(v);
        return out;
    }, []);

    if (!toLoad.length || !result.length) {
        return result;
    }

    const expressions = toLoad.flatMap(x => x.substitutions.map(y => y.expression));
    const res = await loadSubstitutions(expressions, loaders);


    const errors = toLoad.reduce((out, { substitutions, value, fieldName, index, filter }) => {
        const resolvedSubstitutions = res.splice(0, substitutions.length);
        const fieldErrors = [];
        const substitutionValues = resolvedSubstitutions.map(({ range, error }, substitutionIndex) => {
            if (error) {
                fieldErrors.push(error);
                return {};
            }

            const value = [];
            for (const cell of range) {
                const { value: cellValue, error: valueError } = cell;
                if (valueError) {
                    out.push(valueError);
                    return {};
                }
                value.push(cellValue);
            }

            const { index, length } = substitutions[substitutionIndex];
            return { index, length, value };
        });

        if (fieldErrors.length) {
            const joinedErrors = fieldErrors.join('. ');
            out.push(`Unable to resolve substitution(s) for a filter assigned to the field '${fieldName}': ${joinedErrors}`);
            return out;
        }

        const finalValue = replaceByValues(value, substitutionValues);
        result[index] = { ...filter, value: finalValue };
        return out;
    }, []);


    if (errors.length) {
        throw new SubstitutionsError(errors);
    }

    return result;
}

export const OPERATORS = {
    EQ: '=',
    NOTEQ: '!=',
    IN: 'in',
    NOTIN: 'notin',
    INCLUDE: 'include',
    EXCLUDE: 'exclude',
    GT: '>',
    GTEQ: '>=',
    LS: '<',
    LSEQ: '<=',
    START: 'start',
    END: 'end',
    CONTAINS: 'contains',
};

export const JOIN_OPERATOR = {
    AND: 'and',
    OR: 'or'
};

export class FilterConvertionError extends Error {
    constructor(fieldType, value) {
        super(`Cannot convert '${value}' to ${fieldType}`);
    }
}

export const isPicklistValueIsArray = (fieldType, operator) => {
    if (![QA.FieldTypeName.Multipicklist, QA.FieldTypeName.Picklist].includes(fieldType)) { return false; }
    if (fieldType === QA.FieldTypeName.Multipicklist) {
        return true;
    }
    return [OPERATORS.IN, OPERATORS.NOTIN].includes(operator)

}
export const isArrayFilterValue = (fieldType, operator) => fieldType === QA.FieldTypeName.Multipicklist || [OPERATORS.IN, OPERATORS.NOTIN].includes(operator);
export const convertMultipicklistValue = value => value.split(";").map(v => v.trim().toLowerCase());

const COMPARATORS = {
    [OPERATORS.EQ]: (filterValue, recordValue) => {
        if (filterValue instanceof Date) {
            return filterValue?.getTime() === recordValue?.getTime();
        }
        if (Array.isArray(filterValue)) {
            return compareArrayContent(filterValue, recordValue);
        }
        return filterValue === recordValue;
    },
    [OPERATORS.NOTEQ]: (filterValue, recordValue) => {
        if (filterValue instanceof Date) {
            return filterValue?.getTime() !== recordValue?.getTime();
        }
        if (Array.isArray(filterValue)) {
            return !compareArrayContent(filterValue, recordValue)
        }
        return filterValue !== recordValue;
    },
    [OPERATORS.IN]: (filterValue, recordValue) => Array.isArray(recordValue) ? recordValue.every(v => filterValue.includes(v)) : filterValue.includes(recordValue),
    [OPERATORS.NOTIN]: (filterValue, recordValue) => Array.isArray(recordValue) ? recordValue.every(v => !filterValue.includes(v)) : !filterValue.includes(recordValue),
    [OPERATORS.INCLUDE]: (filterValue, recordValue) => filterValue.every(val => recordValue.includes(val)),
    [OPERATORS.EXCLUDE]: (filterValue, recordValue) => filterValue.every(val => !recordValue.includes(val)),
    [OPERATORS.GT]: (filterValue, recordValue) => recordValue > filterValue,
    [OPERATORS.GTEQ]: (filterValue, recordValue) => recordValue >= filterValue,
    [OPERATORS.LS]: (filterValue, recordValue) => recordValue < filterValue,
    [OPERATORS.LSEQ]: (filterValue, recordValue) => recordValue <= filterValue,
    [OPERATORS.START]: (filterValue, recordValue) => String(recordValue).toLowerCase().startsWith(filterValue.toLowerCase()),
    [OPERATORS.END]: (filterValue, recordValue) => String(recordValue).toLowerCase().endsWith(filterValue.toLowerCase()),
    [OPERATORS.CONTAINS]: (filterValue, recordValue) => String(recordValue).includes(filterValue)
};



export function filterRecord(filters, record, sfParsers) {

    const filterResults = filters.reduce((out, filter) => {
        const value = record[filter.columnIndex];
        out.push({ value: filterRecordColumn(filter, value, sfParsers), join: filter.join })
        return out;
    }, []);

    if (filterResults.length === 1) {
        return filterResults[0].value;
    }

    //Calculate all expressions with the AND operator
    const processedAND = filterResults.reduce((out, filter, index) => {
        if (index === 0) {
            return [filter.value];
        }
        if (filter.join === JOIN_OPERATOR.OR) {
            out.push(filter.value);
        }
        else {
            const prevIdx = out.length - 1;
            out[prevIdx] = filter.value && out[prevIdx];
        }
        return out;
    }, []);

    return processedAND.some(v => v);
}

function filterRecordColumn(filter, columnValue, cvt) {
    let value;
    if (filter.isPicklistArray) {
        value = convertMultipicklistValue(columnValue);
    }
    else {
        value = convertSfValue(columnValue, cvt);
    }

    return COMPARATORS[filter.operator](filter.value, value);
}

const DateFields = [QA.FieldTypeName.Date, QA.FieldTypeName.DateTime];
const BooleanValues = ["true", "false"];

function convertSfValue(sfValue, { numberParser, dateTimeParser, enscapeParser }) {
    if (!sfValue) {
        return undefined;
    }

    const value = sfValue.toLowerCase();

    let result = +value;
    if (!isNaN(result)) {
        return result;
    }

    result = numberParser.convert(value);
    if (result) {
        return +result;
    }

    result = dateTimeParser.convert(value);
    if (result) {
        //ignoring time from parser format
        return new Date(result.split(" ")[0])
    }

    result = enscapeParser.convert(value);
    if (result) {
        return result;
    }

    return value;
}


export function filterDateToString(fieldType, date) {
    const val = moment.utc({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate() });
    return fieldType === QA.FieldTypeName.Date ? val.format('YYYY-MM-DD') : val.toISOString();
}

export function filterDateFromString(dateString, utc = false) {
    return (utc ? moment.utc : moment)
        //ignoring time
        (dateString.split("T")[0]).toDate();
}


export function convertFilterValue(
  field,
  value,
  { numberParser },
  { isSubstitution, operator } = {},
  convertSubstitution = true,
) {

    const { dataType: fieldType } = field;

    let substituedValue = value;

    if (isSubstitution && convertSubstitution) {
        const isArrayValue = isArrayFilterValue(fieldType, operator);
        const isMultipickList = fieldType === QA.FieldTypeName.Multipicklist;

        if (isArrayValue && !isMultipickList) {
            substituedValue = substituedValue.split(',');
        }
        else {
            const { value: loadedSubstitution, error } = getExcelValuesConverter(field)(value);
            if (error) {
                throw new FilterConvertionError(fieldType, value);
            }
            substituedValue = loadedSubstitution.toString();
            if (isMultipickList) {
                return convertMultipicklistValue(substituedValue);
            }
        }
    }

    const convert = stringValue => {
        let preparedValue = stringValue.trim();

        if (DateFields.includes(fieldType)) {
            const res = filterDateFromString(preparedValue, true);
            if (isNaN(res)) {
                throw new FilterConvertionError(fieldType, stringValue);
            }
            return res;
        }

        preparedValue = preparedValue.toLowerCase();

        if (QA.NumberFields.includes(fieldType)) {
            let res = +preparedValue;
            if (!isNaN(res)) {
                return res;
            }
            res = numberParser.convert(preparedValue);
            if (res) {
                return +res;
            }
            throw new FilterConvertionError(fieldType, stringValue);
        }

        if (BooleanValues.includes(preparedValue)) {
            return preparedValue === 'true' ? 1 : 0;
        }

        return preparedValue;
    }

    return Array.isArray(substituedValue) ? substituedValue.map(convert) : convert(substituedValue);
}
